import msalConfig from 'services/msal/config';
import { acquireAccessToken } from 'services/msal/_utilities';
/**
 * Helper function for checking the validity of user change against @param member validation rules.
 * @param {*} member Changed component object; object of type 'BaseElement'.
 * @returns {{isValid: boolean, message: string}} Object containing validity and associated message.
 */
export const checkValidityRules = member => {
	let isValid = true;
	let message = '';

	let rules = member.validation.rules;
	let value = member.value && member.value !== null ? member.value : '';

	// member valid if member not required and possesses a default value
	if (
		Object.keys(member.elementConfig.options).length > 0 &&
		!member.elementConfig.options[member.id].Required &&
		!String(value).replace('select an option', '').length > 0
	) {
		return {
			valid: isValid,
			message: message,
		};
	}

	const regEx = rules.regEx.value;
	if (regEx !== null && regEx.length > 0) {
		const re = new RegExp(regEx);
		let newCheck = value.match(re);
		let r = re.test(value);
		if (newCheck && newCheck.length > 0) {
			newCheck = true;
		} else {
			newCheck = false;
		}

		if (newCheck && value && value.length > 0) {
			isValid = isValid && r;
		} else {
			isValid = false;
		}

		if (!newCheck && value && value.length > 0) {
			message = rules.regEx.message;
		}
	}

	const invalidIndex = rules.invalidIndex.value;
	if (invalidIndex !== null && invalidIndex > -1) {
		const selectedIndex =
			member.elementConfig.options[member.id].ApplicationData.findIndex(
				el => el.Value === value
			) > -1
				? member.elementConfig.options[
						member.id
				  ].ApplicationData.indexOf(value)
				: 0;
		const newCheck = selectedIndex !== invalidIndex;
		isValid = isValid && newCheck;
		if (!newCheck) {
			message = rules.invalidIndex.message;
		}
	}

	const maxCount = rules.maxCount.value;
	if (maxCount !== null) {
		let count = value.split('~~').length;
		const newCheck = count <= maxCount;
		isValid = isValid && newCheck;
		if (!newCheck) {
			message =
				rules.maxCount.message +
				maxCount +
				'.  (You selected ' +
				count +
				')';
		}
	}

	const min_count = rules.minCount.value;
	if (min_count !== null) {
		let count = value ? value : 0;
		const new_check = count >= min_count;
		isValid = isValid && new_check;
		if (!new_check) {
			const displayCount = Number.isInteger(count) ? count : 0;
			message =
				rules.minCount.message +
				min_count +
				'.  (You selected ' +
				displayCount +
				')';
		}
	}

	const maxLength = rules.maxLength.value;
	if (maxLength !== null) {
		const newCheck = value.length <= maxLength;
		isValid = isValid && newCheck;
		if (!newCheck) {
			message = rules.maxLength.message;
		}
	}

	const minLength = rules.minLength.value;
	if (
		minLength !== null &&
		value !== null &&
		value.toString().trim() !== ''
	) {
		const newCheck = value.length >= minLength;
		isValid = isValid && newCheck;
		if (!newCheck) {
			message = rules.minLength.message;
		}
	}

	const required = rules.required.value;
	if (required !== null && required) {
		const newCheck = value.toString().trim() !== '';
		isValid = isValid && newCheck;
		if (!newCheck) {
			message = rules.required.message;
		}
	}

	// validation message exclusive to touched components
	message = member.validation.touched ? message : '';

	return {
		valid: isValid,
		message: message,
	};
};

export const getAppIndex = (oldState, targetApp) => {
	for (let i in oldState.allForms) {
		if (targetApp === oldState.allForms[i].id) {
			return i;
		}
	}
	return 0;
};

export const getFormValidity = (members, checkForVisibility) => {
	let valid = true;
	Object.keys(members).every(mId => {
		const member = members[mId];
		const trueValidity =
			checkForVisibility && member.visible
				? member.validation.valid
				: true;
		valid = valid && trueValidity;
		return valid;
	});
	return valid;
};

export const getOverallValidity = allForms => {
	let validity = true;

	for (let i in allForms) {
		validity = validity && allForms[i].valid;
	}

	return validity;
};

export const getFormIdByAppId = (state, appId) => {
	let formId = null;
	Object.keys(state.allForms).forEach(form => {
		if (state.allForms[form].id === appId) {
			formId = form;
		}
	});
	return formId;
};

export const objectInsertAtIndex = (dict, key, value, index) => {
	let i = 0;
	for (let prop in dict) {
		if (i === index) {
			dict[key] = value;
		}
		if (i >= index) {
			const tempVal = dict[prop];
			delete dict[prop];
			dict[prop] = tempVal;
		}
		i++;
	}
	return dict;
};

export const updateObject = (oldObject, updatedProperties) => {
	return {
		...oldObject,
		...updatedProperties,
	};
};

export const deepCloneObject = obj => {
	if (obj === null || typeof obj !== 'object') {
		return obj;
	}
	var temporary = obj.constructor();
	for (var key in obj) {
		temporary[key] = deepCloneObject(obj[key]);
	}
	return temporary;
};

export const deepEqualObject = (obj1, obj2) => {
	const areObjects = isObject(obj1) && isObject(obj2);

	//if obj1 and obj2 are not objects, compare the values
	if (!areObjects) {
		return obj1 === obj2;
	}

	const keys1 = Object.keys(obj1);
	const keys2 = Object.keys(obj2);

	if (keys1.length !== keys2.length) {
		return false;
	} else {
		//check to see if all keys in obj1 are in obj2 and vice versa
		const obj1ContainsKeys2 = keys2.every(k2 => keys1.includes(k2));
		const obj2ContainsKeys1 = keys1.every(k1 => keys2.includes(k1));
		if (!(obj1ContainsKeys2 && obj2ContainsKeys1)) {
			return false;
		}
	}

	let valid = keys1.every(key => {
		const val1 = obj1[key];
		const val2 = obj2[key];
		return deepEqualObject(val1, val2);
	});
	return valid;
};

const isObject = obj => {
	return obj != null && typeof obj === 'object';
};

export const getComponentByComponentId = (childId, members, prefix = null) => {
	let component = null;
	Object.keys(members).forEach(member => {
		if (members[member].id === childId) {
			if (prefix) {
				if (prefix === member.split('_')[0]) {
					component = members[member];
				}
			} else {
				component = members[member];
			}
		}
	});
	return component;
};

export const isNullOrUndefined = (childComponentName, members) => {
	return (
		childComponentName === null ||
		members[childComponentName] === null ||
		childComponentName === undefined ||
		members[childComponentName] === undefined
	);
};

/**
 * Asynchronous method for retrieving value of session variable with key @param key
 * @param {String} key key of target session variable
 * @returns {Promise} Resulting promise of request, ultimately resolving to type {Object} if successful.
 * @example
 * // returns value of key 'UsersList' in session
 * getSession('UsersList')
 */
export const getSession = async key => {
	const token = await acquireAccessToken(msalConfig.requests.api);
	return fetch('/Session/GetSessionValue/', {
		method: 'POST',
		credentials: 'same-origin',
		headers: {
			'Content-Type': 'application/json',
			Authorization: `Bearer ${token}`,
		},
		body: JSON.stringify({ key: key }),
	})
		.then(response => response.json())
		.then(response => response)
		.catch(() => null);
};

/**
 * Asynchronous method for setting value of existing session entry.
 *
 * @function
 * @param {string} key unique identifier of session entry of interest.
 * @param {any} value new value for session entry of interest
 * @returns {Promise|null} nullable resolved response object if successful.
 */
export const setSession = async (key, value) => {
	const token = await acquireAccessToken(msalConfig.requests.api);
	return fetch('/Session/SetSessionValue/', {
		method: 'POST',
		credentials: 'same-origin',
		headers: {
			'Content-Type': 'application/json',
			Authorization: `Bearer ${token}`,
		},
		body: JSON.stringify({
			key: key,
			value: value,
		}),
	})
		.then(response => {
			return response;
		})
		.catch(() => null);
};

export const clearSession = () => {
	return fetch('/Session/ClearSession', {
		method: 'DELETE',
		credentials: 'same-origin',
		headers: { 'Content-Type': 'application/json' },
	})
		.then(response => {
			if (!response.ok) {
				throw new Error(
					`Error occurred with status ${response.status}.`
				);
			}
			return response;
		})
		.catch(() => null);
};

/**
 * For the given numeric `key`, return the corresponding message type string
 * @param {string} key - essentially an enum corresponding to the message type
 */
export const lookupMessageKey = key => {
	switch (key) {
		case 0:
			return 'Notice';
		case 1:
			return 'Info';
		case 2:
			return 'Warning';
		case 3:
			return 'Error';
		case 4:
			return 'Critical';
		case 5:
			return 'Success';
		default:
			return 'Notice';
	}
};

/**
 * HTTP transport layer.
 * @typedef {Object} IMSPacket
 * @property {*} payload
 * @property {Object[]} messages
 * @property {number} messages[].key
 * @property {string} messages[].value
 * @property {number} responseCode
 */

/**
 * Creates HTTP transport layer payload.
 * @param {*} payload
 * @param {Object[]} messages
 * @param {number} messages.key
 * @param {string} messages.value
 * @param {number} responseCode
 * @returns {IMSPacket}
 */
export const WrapImsPacket = (
	payload = {},
	messages = [],
	responseCode = 200
) => {
	return {
		payload: payload,
		messages: messages,
		responseCode: responseCode,
	};
};

export const generateMemberKey = member => {
	return `${member.id}_${member.label.toLowerCase()}`;
};

export const getMemberFromMembers = (members, targetId) => {
	let targetMember = null;
	Object.values(members).forEach(member => {
		if (member.id === targetId) {
			targetMember = member;
		}
	});
	return targetMember;
};
